import React from "react";
import {App as AntdApp, Button, InputNumber, Modal, Space, Typography, Upload, Progress} from "antd";
import {FilePdfOutlined, UploadOutlined} from "@ant-design/icons";
import api from "@/api/axios.js";

const {Text} = Typography;
const PDF_POINTS_PER_INCH = 72;
const MM_PER_INCH = 25.4;
const PDF_SIZE_EPSILON_PT = 0.001;
const WEBP_QUALITY_MIN = 50;
const WEBP_QUALITY_MAX = 100;
const WEBP_QUALITY_DEFAULT = 90;
const RASTER_DPI_MIN = 72;
const RASTER_DPI_MAX = 300;
const RASTER_DPI_DEFAULT = 200;

let pdfLibPromise = null;

const loadPdfLib = async () =>
{
	if (!pdfLibPromise)
	{
		pdfLibPromise = import("pdf-lib");
	}
	return pdfLibPromise;
};

const ptToMm = (pt) => (pt * MM_PER_INCH) / PDF_POINTS_PER_INCH;
const PDF_SIZE_EPSILON_MM = ptToMm(PDF_SIZE_EPSILON_PT);
const ptToSafeMmInt = (pt) => Math.max(1, Math.floor(ptToMm(pt) + PDF_SIZE_EPSILON_MM));

/**
 * Modal per caricare un PDF e convertirlo in pagine WebP.
 *
 * Props:
 * - flyerId: ID del volantino
 * - open: se il modal è aperto
 * - onClose: callback chiusura
 * - onComplete: callback completamento con { pagesCount, pageWidth, pageHeight }
 */
export default function UploadPdfModal({open, onClose, flyerId, onComplete, previewScaleDefault = 1, preserveZones = false})
{
	const {message} = AntdApp.useApp();
	const parseSeqRef = React.useRef(0);
	const [filePdf, setFilePdf] = React.useState(null);
	const [previewScale, setPreviewScale] = React.useState(previewScaleDefault);
	const [webpQuality, setWebpQuality] = React.useState(WEBP_QUALITY_DEFAULT);
	const [rasterDpi, setRasterDpi] = React.useState(RASTER_DPI_DEFAULT);
	const [pdfWidthMm, setPdfWidthMm] = React.useState(null);
	const [pdfHeightMm, setPdfHeightMm] = React.useState(null);
	const [originalPdfWidthMm, setOriginalPdfWidthMm] = React.useState(null);
	const [originalPdfHeightMm, setOriginalPdfHeightMm] = React.useState(null);
		const [isUploading, setIsUploading] = React.useState(false);
		const [progress, setProgress] = React.useState(0);

		const dimensionsReady = Number.isFinite(pdfWidthMm) && Number.isFinite(pdfHeightMm);
		const originalDimensionsReady = Number.isFinite(originalPdfWidthMm) && Number.isFinite(originalPdfHeightMm);
		const isDimensionsValid = dimensionsReady
			&& pdfWidthMm > 0
			&& pdfHeightMm > 0;
	const isQualityValid = Number.isFinite(webpQuality)
		&& webpQuality >= WEBP_QUALITY_MIN
		&& webpQuality <= WEBP_QUALITY_MAX;
	const isRasterDpiValid = Number.isFinite(rasterDpi)
		&& rasterDpi >= RASTER_DPI_MIN
		&& rasterDpi <= RASTER_DPI_MAX;
	const estimatedRasterSize = React.useMemo(() =>
	{
		if (!isDimensionsValid || !isRasterDpiValid)
			return null;

		return {
			width: Math.max(1, Math.round((pdfWidthMm * rasterDpi) / MM_PER_INCH)),
			height: Math.max(1, Math.round((pdfHeightMm * rasterDpi) / MM_PER_INCH))
		};
	}, [isDimensionsValid, isRasterDpiValid, pdfWidthMm, pdfHeightMm, rasterDpi]);

	// Reset quando si chiude
	React.useEffect(() =>
	{
		if (!open)
		{
			parseSeqRef.current += 1;
			setFilePdf(null);
			setPreviewScale(previewScaleDefault);
			setWebpQuality(WEBP_QUALITY_DEFAULT);
			setRasterDpi(RASTER_DPI_DEFAULT);
			setPdfWidthMm(null);
			setPdfHeightMm(null);
			setOriginalPdfWidthMm(null);
			setOriginalPdfHeightMm(null);
			setIsUploading(false);
			setProgress(0);
			return;
		}
		setPreviewScale(previewScaleDefault);
	}, [open, previewScaleDefault]);

	React.useEffect(() =>
	{
		if (!open) return;

		let cancelled = false;
		api.get("/api/settings")
			.then(({data}) =>
			{
				if (cancelled) return;
				const nextDpi = Number(data?.settings?.flyerPages?.ocrDpi);
				if (Number.isFinite(nextDpi) && nextDpi >= RASTER_DPI_MIN && nextDpi <= RASTER_DPI_MAX)
					setRasterDpi(nextDpi);
			})
			.catch(() => null);

		return () =>
		{
			cancelled = true;
		};
	}, [open]);

	const handleUpload = async () =>
	{
		if (isUploading) return;

		if (!flyerId) return message.warning("FlyerId non disponibile");

		if (!filePdf) return message.warning("Seleziona un file PDF");

			if (!Number.isFinite(pdfWidthMm) || !Number.isFinite(pdfHeightMm) || pdfWidthMm <= 0 || pdfHeightMm <= 0)
				return message.warning("Inserisci larghezza/altezza PDF valide (> 0)");

		if (!isQualityValid)
			return message.warning(`Qualità WebP non valida (${WEBP_QUALITY_MIN}–${WEBP_QUALITY_MAX})`);

		if (!isRasterDpiValid)
			return message.warning(`DPI OCR non valido (${RASTER_DPI_MIN}–${RASTER_DPI_MAX})`);

		setIsUploading(true);
		setProgress(10);

		try
		{
			const formData = new FormData();
			formData.append("pdf", filePdf, filePdf.name);
			formData.append("pdfWidthMm", String(pdfWidthMm));
			formData.append("pdfHeightMm", String(pdfHeightMm));
			formData.append("previewScale", String(previewScale));
			formData.append("webpQuality", String(webpQuality));
			formData.append("rasterDpi", String(rasterDpi));
			if (preserveZones) formData.append("preserveZones", "1");

			setProgress(30);

			const res = await api.post(`/api/flyers/${flyerId}/upload-pdf`, formData);

			setProgress(100);

			if (res.data?.ok)
			{
				message.success(`${res.data.pagesCount} pagine convertite con successo!`);
				onComplete?.({
					pagesCount: res.data.pagesCount,
					pageWidth: res.data.pageWidth,
					pageHeight: res.data.pageHeight,
					rasterDpi: res.data.rasterDpi
				});
				onClose?.();
			}
			else
			{
				throw new Error(res.data?.error || "Errore sconosciuto");
			}
		}
		catch (err)
		{
			console.error(err);
			message.error(err?.response?.data?.error || err.message || "Errore durante la conversione");
		}
		finally
		{
			setIsUploading(false);
		}
	};

	const parseFirstPageSizeMm = async (file) =>
	{
		const arrayBuffer = await file.arrayBuffer();
		try
		{
			const {PDFDocument} = await loadPdfLib();
			const pdfDoc = await PDFDocument.load(arrayBuffer, {ignoreEncryption: true});
			if (pdfDoc.getPageCount() <= 0) return null;

			// Default richiesto: MediaBox.
			const page = pdfDoc.getPage(0);
			const box = page.getMediaBox();

			if (!box || !Number.isFinite(box.width) || !Number.isFinite(box.height) || box.width <= 0 || box.height <= 0)
			{
				return null;
			}

			return {
				widthMm: ptToSafeMmInt(box.width),
				heightMm: ptToSafeMmInt(box.height)
			};
		}
		catch
		{
			// Fallback best-effort per PDF non standard/non parseabili da pdf-lib.
			const text = new TextDecoder("latin1").decode(new Uint8Array(arrayBuffer));
			const getBoxByName = (boxName) =>
			{
				const boxRegex = new RegExp(`/${boxName}\\s*\\[\\s*(-?\\d+(?:\\.\\d+)?)\\s+(-?\\d+(?:\\.\\d+)?)\\s+(-?\\d+(?:\\.\\d+)?)\\s+(-?\\d+(?:\\.\\d+)?)\\s*\\]`);
				return text.match(boxRegex);
			};
			const match = getBoxByName("MediaBox") || getBoxByName("CropBox") || getBoxByName("TrimBox");
			if (!match) return null;

			const x1 = Number(match[1]);
			const y1 = Number(match[2]);
			const x2 = Number(match[3]);
			const y2 = Number(match[4]);
			if (![x1, y1, x2, y2].every(Number.isFinite)) return null;

			const widthPt = Math.abs(x2 - x1);
			const heightPt = Math.abs(y2 - y1);
			if (!widthPt || !heightPt) return null;

			return {
				widthMm: ptToSafeMmInt(widthPt),
				heightMm: ptToSafeMmInt(heightPt)
			};
		}
	};

	const beforeUpload = async (file) =>
	{
		const isPdf = file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf");
		if (!isPdf)
		{
			message.error("Solo file PDF sono supportati");
			return false;
		}

		// Reset immediato per evitare che restino visibili valori precedenti.
		const seq = parseSeqRef.current + 1;
		parseSeqRef.current = seq;
		setFilePdf(file);
		setPdfWidthMm(null);
		setPdfHeightMm(null);
		setOriginalPdfWidthMm(null);
		setOriginalPdfHeightMm(null);

		try
		{
			const size = await parseFirstPageSizeMm(file);
			if (parseSeqRef.current !== seq) return false;
			if (size)
			{
				setOriginalPdfWidthMm(size.widthMm);
				setOriginalPdfHeightMm(size.heightMm);
				setPdfWidthMm(size.widthMm);
				setPdfHeightMm(size.heightMm);
			}
			else
			{
				message.warning("Dimensioni PDF non rilevate automaticamente. Inseriscile manualmente.");
			}
		}
		catch
		{
			if (parseSeqRef.current !== seq) return false;
			message.warning("Impossibile leggere le dimensioni del PDF. Inseriscile manualmente.");
		}
		return false; // Prevent auto upload
	};

	return (
		<Modal
			open={open}
			onCancel={isUploading ? undefined : onClose}
			title="Carica PDF Volantino"
			width={500}
			maskClosable={!isUploading}
			closable={!isUploading}
			className="sis-modal"
			footer={null}
			destroyOnHidden
		>
			<Space direction="vertical" size="middle" style={{width: "100%"}}>
				<Text type="secondary">
					Carica il PDF del volantino. Le pagine verranno convertite in immagini WebP per la pre-elaborazione.
				</Text>

				<Upload.Dragger
					beforeUpload={beforeUpload}
					accept=".pdf,application/pdf"
					showUploadList={false}
					disabled={isUploading}
					style={{background: "rgba(255,255,255,0.6)"}}
				>
					<p className="ant-upload-drag-icon"><FilePdfOutlined/></p>
					<p className="ant-upload-text">
						{filePdf ? filePdf.name : "Trascina il PDF qui"}
					</p>
					<p className="ant-upload-hint">Oppure clicca per selezionare</p>
				</Upload.Dragger>

				<Space style={{width: "100%", justifyContent: "space-between"}}>
						<Text>Larghezza (mm):</Text>
						<InputNumber
							min={1}
							step={1}
							precision={0}
							value={Number.isFinite(pdfWidthMm) ? pdfWidthMm : undefined}
							onChange={(value) => setPdfWidthMm(typeof value === "number" ? value : null)}
						style={{width: 220}}
						disabled={isUploading || !filePdf}
					/>
				</Space>

				<Space style={{width: "100%", justifyContent: "space-between"}}>
						<Text>Altezza (mm):</Text>
						<InputNumber
							min={1}
							step={1}
							precision={0}
							value={Number.isFinite(pdfHeightMm) ? pdfHeightMm : undefined}
							onChange={(value) => setPdfHeightMm(typeof value === "number" ? value : null)}
						style={{width: 220}}
						disabled={isUploading || !filePdf}
					/>
					</Space>
					{originalDimensionsReady && (
						<Text type="secondary" style={{fontSize: 12}}>
							Prima pagina rilevata: {originalPdfWidthMm} x {originalPdfHeightMm} mm. Le pagine piu piccole verranno centrate automaticamente.
						</Text>
					)}

					<Space style={{width: "100%", justifyContent: "space-between"}}>
						<Text>Qualità WebP ({WEBP_QUALITY_MIN}–{WEBP_QUALITY_MAX}):</Text>
					<InputNumber
						min={WEBP_QUALITY_MIN}
						max={WEBP_QUALITY_MAX}
						step={1}
						precision={0}
						value={Number.isFinite(webpQuality) ? webpQuality : undefined}
						onChange={(value) => setWebpQuality(typeof value === "number" ? value : NaN)}
						style={{width: 220}}
						disabled={isUploading || !filePdf}
					/>
				</Space>
				<Text type="secondary" style={{fontSize: 12}}>
					Influisce solo sulle pagine finali, non sulle preview.
				</Text>

				<Space style={{width: "100%", justifyContent: "space-between"}}>
					<Text>DPI OCR pagine:</Text>
					<InputNumber
						min={RASTER_DPI_MIN}
						max={RASTER_DPI_MAX}
						step={1}
						precision={0}
						value={Number.isFinite(rasterDpi) ? rasterDpi : undefined}
						onChange={(value) => setRasterDpi(typeof value === "number" ? value : NaN)}
						style={{width: 220}}
						disabled={isUploading || !filePdf}
					/>
				</Space>
				<Text type="secondary" style={{fontSize: 12}}>
					{estimatedRasterSize
						? `Dimensione stimata pagine: circa ${estimatedRasterSize.width} x ${estimatedRasterSize.height} px.`
						: `Default impostazioni ${RASTER_DPI_DEFAULT} DPI. La stima comparirà dopo il rilevamento delle dimensioni PDF.`}
				</Text>

				{isUploading && (
					<Progress percent={progress} status="active"/>
				)}

				<Space style={{width: "100%", justifyContent: "flex-end"}}>
					<Button onClick={onClose} disabled={isUploading}>
						Annulla
					</Button>
					<Button
						type="primary"
						onClick={handleUpload}
						loading={isUploading}
						disabled={!filePdf || isUploading || !isDimensionsValid || !isQualityValid || !isRasterDpiValid}
						icon={<UploadOutlined/>}
					>
						Carica Volantino
					</Button>
				</Space>
			</Space>
		</Modal>
	);
}
